/**
 * ABAS DE CADA SISTEMA — o que o administrador escolhe ao criar um acesso
 * SOMENTE VISUALIZAÇÃO ("quais abas do sistema essa pessoa consegue ver").
 *
 * As chaves são as MESMAS que cada sistema usa internamente (src/config.ts dos
 * sistemas por aba; rota/menu dos outros). Não reaproveita `SYSTEMS[].modules`
 * de propósito: aquela lista é a dos atalhos dos cards e está incompleta (o
 * Laboratório tem 13 abas e o card mostra 8, por exemplo).
 *
 * Ficam de fora a aba "Usuários" (só administrador — e somente visualização
 * nunca é administrador) e "Solicitar Atualização" (é um pedido, não consulta).
 * Aba nova num sistema: acrescente aqui com a mesma chave do sistema.
 */
import type { SystemId } from "./integration";

export interface AbaDoSistema {
  key: string;
  label: string;
}

const a = (key: string, label: string): AbaDoSistema => ({ key, label });

export const ABAS_POR_SISTEMA: Record<SystemId, AbaDoSistema[]> = {
  rh: [
    a("dashboard", "Dashboard"),
    a("colaboradores", "Colaboradores"),
    a("treinamentos", "Plano de Ação"),
    a("engajamento", "Pesquisa de Clima"),
    a("contratacoes", "Contratações"),
    a("triagem", "Triagem de Currículo"),
    a("disc", "DISC"),
    a("entrevistas", "Entrevistas"),
    a("demissoes", "Demissões"),
    a("promocoes", "Promoções"),
    a("avaliacoes", "Avaliações"),
    a("desempenho", "Avaliação de Desempenho"),
    a("endomarketing", "Endomarketing"),
    a("cargos", "Cargos e Salários"),
  ],
  dp: [
    a("dashboard", "Dashboard"),
    a("colaboradores", "Colaboradores"),
    a("contratacoes", "Contratações e Demissões"),
    a("folha_pagamento", "Folha de Pagamento"),
    a("adiantamento", "Folha de Pagamento · Adiantamento Salarial"),
    a("holerites", "Folha de Pagamento · Holerites"),
    a("espelho_ponto", "Folha de Pagamento · Espelho de Ponto"),
    a("comprovantes", "Folha de Pagamento · Comprovantes"),
    a("assinaturas", "Assinaturas e Holerites"),
    a("beneficios", "Benefícios"),
    a("guia-beneficios", "Guia de Benefícios"),
    a("venc_cnh", "Vencimentos · CNH"),
    a("venc_aso", "Vencimentos · ASO"),
    a("experiencias", "Vencimentos · Experiências"),
    a("venc_nr", "Vencimentos · NRs"),
    a("venc_pcmso", "Vencimentos · PCMSO"),
    a("venc_pgr", "Vencimentos · PGR"),
    a("documentos", "Documentos"),
    a("folha_ponto", "Folha de Ponto"),
    a("horas_extras", "Horas Extras"),
    a("rescisoes", "Rescisões"),
    a("processos_trabalhistas", "Processos Trabalhistas"),
    a("ferias", "Férias"),
    a("treinamentos", "Plano de Ação"),
  ],
  qualidade: [
    a("dashboard", "Dashboard"),
    a("nao_conformidades", "Não Conformidades"),
    a("acoes_corretivas", "Ações Corretivas"),
    a("auditorias", "Auditorias"),
    a("satisfacao_cliente", "Satisfação do Cliente"),
    a("retrabalho", "Retrabalho"),
    a("desempenho", "Satisfação e Retrabalho por Equipe"),
    a("conformidade_normas", "Conformidade (Normas)"),
    a("documentos", "Documentos"),
    a("gestores_setor", "Gestores por Setor"),
  ],
  almoxarifado: [
    a("dashboard", "Dashboard"),
    a("itens", "Itens"),
    a("alertas", "Alertas de Estoque"),
    a("recebimentos", "Recebimento de Materiais"),
    a("movimentacoes", "Movimentações"),
    a("inventarios", "Inventários"),
    a("solicitacoes", "Solicitações"),
    a("documentos", "Documentos"),
  ],
  suprimentos: [
    a("dashboard", "Dashboard"),
    a("materiais", "Cadastro de Materiais"),
    a("fornecedores", "Cadastro de Fornecedores"),
    a("compras", "Compras"),
    a("orcamentos", "Orçamentos"),
    a("notas", "Entrada de Notas Fiscais"),
    a("estoque", "Entrada e Saída"),
    a("impostos", "Impostos"),
  ],
  planejamento: [
    a("dashboard", "Dashboard"),
    a("agenda", "Agenda"),
    a("atividades", "Atividades"),
    a("satisfacao", "Satisfação"),
    a("retrabalho", "Retrabalho"),
    a("veiculos", "Veículos"),
    a("equipamentos", "Equipamentos de Ensaio"),
    a("separacao_material", "Separação de Material"),
    a("colaboradores", "Equipe"),
  ],
  operacional: [
    a("dashboard", "Dashboard"),
    a("agenda", "Agenda"),
    a("controle_trafos", "Controle de Trafos"),
    a("treinamentos", "Treinamentos"),
    a("retrabalho", "Retrabalho"),
    a("acidentes", "Acidentes / SST"),
    a("satisfacao", "Satisfação"),
    a("veiculos", "Veículos"),
    a("manutencao_veiculos", "Manutenção de Veículos"),
    a("relatorio_inspecao", "Relatório de Inspeção"),
    a("equipamentos", "Equipamentos de Ensaio"),
    a("avaliar_folga", "Avaliar Folga"),
    a("liberacoes", "Liberações"),
    a("aprovar_agendamento", "Aprovar Agendamento"),
    a("painel_relatorios", "Relatórios"),
    a("almoxarifado", "Almoxarifado"),
    a("colaboradores", "Equipe"),
  ],
  laboratorio: [
    a("dashboard", "Dashboard"),
    a("protocolo", "Protocolo"),
    a("analises", "Análises"),
    a("material", "Material do cliente"),
    a("seringas", "Controle de Seringas"),
    a("equipamentos_ensaio", "Equipamentos de Ensaio"),
    a("quebras", "Quebra de frasco/seringa"),
    a("aprovacao", "Aprovação de relatório"),
    a("historico", "Relatórios"),
    a("equipamentos", "Equipamentos"),
    a("limites", "Limites normativos"),
    a("criterios", "Critérios do diagnóstico"),
    a("razoes", "Razões NBR 7274"),
    a("emms", "Análise de EMMS"),
  ],
  colaborador: [
    a("agenda", "Minha Agenda"),
    a("trafos_reforma", "Trafos em Reforma"),
    a("inspecao_veiculo", "Inspeção de Veículos"),
    a("solicitar_folga", "Solicitar Folga"),
  ],
  ponto: [
    a("inicio", "Início"),
    a("bater", "Bater ponto"),
    a("espelho", "Meu mês"),
    a("folhas", "Folhas de ponto"),
    a("extras", "Horas extras"),
    a("banco", "Banco de horas"),
    a("documentos", "Documentos"),
    a("dados", "Meus dados"),
  ],
  desenvolvedor: [
    a("painel", "Painel"),
    a("solicitacoes", "Solicitações"),
    a("agenda", "Agenda"),
    a("lembretes", "Lembretes"),
    a("atualizacoes", "Atualizações"),
    a("auditoria", "Auditoria"),
    a("backup", "Backup"),
  ],
  comercial: [
    a("dashboard", "Dashboard"),
    a("priorizacao", "Priorização (IA)"),
    // "Interações" não tem tela própria: ficam dentro da ficha do cliente.
    a("clientes", "Carteira / Clientes (com interações)"),
    a("manutencao", "Manutenção Programada"),
    a("aneel", "Desligamentos ANEEL"),
    a("industrias", "Novas Indústrias (dentro de Desligamentos ANEEL)"),
    a("relatorios", "Relatórios"),
    a("carteira", "Carteiras (ADM)"),
  ],
  gestao_comercial: [
    a("kanban", "Gestão de Propostas"),
    a("resumo", "Resumo"),
    a("clientes", "Clientes (com follow-up)"),
  ],
  proposta: [
    a("dashboard", "Dashboard"),
    a("nova_proposta", "Nova Proposta (Chat)"),
    a("historico", "Banco de Dados de Propostas"),
    // As duas abaixo são de administrador no Propostas: só aparecem se a conta de lá for admin.
    a("tabela_valores", "Tabela de Valores"),
    a("configuracoes", "Configurações"),
    a("numero_sequencia", "Cadastro de OS"),
  ],
  relatorios: [
    a("fotografico", "Fotográfico"),
    a("spda", "SPDA"),
    a("rdo", "RDO de Montagem"),
    a("tecnico", "Relatório Técnico"),
    a("gastos", "Relatório de Gastos"),
    a("os", "Ordens de Serviço"),
    a("banco_relatorios", "Banco de Relatórios"),
    a("mesclar", "Mesclar Relatórios"),
  ],
};
